import { Vector2, Mesh, Vector3, TransformNode, Node, Quaternion } from '@babylonjs/core';
import { BaseShape } from './IShape';
import store from '@/store';
import { HelpPoint } from './helpPoint';
import { SlotItem } from './slotItem';
import { ElementMenuItem } from './elementMenuItem';
import { wallManager } from './wallManager';

export enum ElementType {
    BODY,
    FRONT,
    INSIDE,
    HANDLE,
    LEGS,
    LEGS_BASE,
    WALL,
    HINGE,
    WALL_SUPPORT,
    SLOT,
    MATTRESS,
    OTHER,
    BEDHEAD,
    KEA_RING,
    BACK,
}

export enum SpecyficType {
    DRAWER,
    SHELF,
    HANGER,
    VERTICAL_WALL,
    HORIZONTAL_WALL,
    OTHER,
    DRAWER_FRONT,
    REGULAR_FRONT,
}

export default class Element extends BaseShape {
    private static selectableTypes = [
        ElementType.FRONT, ElementType.INSIDE,
    ];

    private static colorableTypes = [
        ElementType.BODY,
        ElementType.FRONT,
        ElementType.INSIDE,
        ElementType.WALL,
        ElementType.BEDHEAD,
    ];

    private static staticTypes = [
        ElementType.BODY,
        ElementType.LEGS,
        ElementType.LEGS_BASE,
        ElementType.MATTRESS,
        ElementType.OTHER,
        ElementType.BEDHEAD,
        ElementType.KEA_RING,
    ];

    private static rotatedTypes: ElementType[] = [
        //ElementType.BODY, ElementType.WALL,
    ];

    public isSelected: boolean = false;
    public slot!: SlotItem;
    public menuElement!: ElementMenuItem;

    private _width: number = 0;
    private _height: number = 0;
    private _depth: number = 0;
    private _type!: ElementType;
    private _specyficType: SpecyficType = SpecyficType.OTHER;
    private _bindings: Element[] = [];
    private _price: number = 0;

    public get width(): number { return this._width; }
    public get height(): number { return this._height; }
    public get depth(): number { return this._depth; }
    public get type(): ElementType { return this._type; }
    public get specyficType(): SpecyficType { return this._specyficType; }
    public get isDrawer(): boolean { return this._specyficType === SpecyficType.DRAWER; }
    public get bindings(): Element[] { return this._bindings; }
    public get price(): number { return this._price; }

    public get center(): Vector3 {
        return this.rootPosition;

        // const nonUniformMeshes = this.meshes.filter((x) => x.position.isNonUniform);
        // if (!nonUniformMeshes.length) { return Vector3.Zero(); }
        // return nonUniformMeshes[0].position.clone();
    }

    public set center(position: Vector3) {
        this.moveMeshes(position);
    }

    public get isVisible(): boolean {
        return this.meshes.some((x) => x.visibility === 1);
    }

    public set isVisible(visibility: boolean) {
        this.meshes.forEach((x) => {
            x.visibility = visibility ? 1 : 0;
            x.isPickable = this.type === ElementType.HANDLE ? false : visibility;
        });
    }

    public get isSelectable(): boolean {
        return (
            Element.selectableTypes.includes(this.type) ||
            this.isUnusedWall()
        );
    }

    public isUnusedWall(): boolean {
        return this.type === ElementType.WALL && wallManager.inUnused(this);
    }

    public get isStatic(): boolean {
        return Element.staticTypes.includes(this.type);
    }

    public get isRotated(): boolean {
        return Element.rotatedTypes.includes(this.type);
    }

    public get helpPoints(): HelpPoint[] {
        return HelpPoint.create(this.meshes);
    }

    public get isColorable(): boolean {
        return Element.colorableTypes.includes(this.type);
    }

    public constructor(
        public model: string,
        public light: string,
        public shaderName: string,
        public position: Vector2,
        public shape: Vector2,
        public meshes: Mesh[],
    ) {
        super(position, shape);
        this.defineType();
        this.setBoundingInfo();
        this.setVisibility();
        this.setPrice();
    }

    public clone(): Element {
        return new Element(
            this.model,
            this.light,
            this.shaderName,
            this.position.clone(),
            this.shape.clone(),
            this.cloneMeshes(),
        );
    }

    public remove() {
        this.meshes.forEach((x) => x.dispose());
        for (let i = 0; i < this._bindings.length; i++) {
            this.unbind(this._bindings[i]);
        }
    }

    public toggleVisibility() {
        this.isVisible = !this.isVisible;
    }

    public rotate(angle: number, axis: Vector3) {
        const mesh = (this.root.getChildren()[0] as TransformNode);
        const radians = angle * Math.PI / 180;
        mesh.rotate(axis, radians);
    }

    public get rotation(): Quaternion {
        return (this.root.getChildren()[0] as TransformNode).rotationQuaternion!.clone();
    }

    public set rotation(rotation: Quaternion) {
        (this.root.getChildren()[0] as TransformNode).rotationQuaternion = rotation;
    }

    public bind(other: Element) {
        this._bindings.push(other);
        other._bindings.forEach((binding) => {
            if (this._bindings.indexOf(binding) === -1) {
                this.bind(binding);
            }
        });
        other._bindings.push(this);
    }

    public unbind(other: Element) {
        this._bindings.splice(this.bindings.indexOf(other), 1);
        other._bindings.forEach((binding) => {
            if (this._bindings.indexOf(binding) !== -1) {
                this.unbind(binding);
            }
        });
        other._bindings.splice(other.bindings.indexOf(this), 1);
    }

    public get root(): Mesh {
        return this.meshes.find((x) => x.name === '__root__')!;
    }

    public get rootPosition(): Vector3 {
        return (this.root.getChildren()[0] as TransformNode).position.clone();
    }

    public set rootPosition(position: Vector3) {
        (this.root.getChildren()[0] as TransformNode).position = position;
    }

    public freeze(value: boolean) {
        this.meshes.forEach((mesh) => {
            if (value) {
                mesh.freezeWorldMatrix();
                mesh.doNotSyncBoundingInfo = true;
            } else {
                mesh.unfreezeWorldMatrix();
                mesh.doNotSyncBoundingInfo = false;
            }
        })
    }

    private cloneMeshes(): Mesh[] {
        const root = this.root;
        const clonedRoot = root.clone(root.name, null);
        clonedRoot.material = clonedRoot.material.clone(clonedRoot.material.name);
        const clonedMeshes = clonedRoot.getChildMeshes() as Mesh[];
        clonedMeshes.forEach((child) => {
            const splittedName = child.name.split('.');
            const lastPart = splittedName[splittedName.length - 1];
            child.material = child.material.clone(child.material.name);
            child.name = lastPart;
        });
        return [clonedRoot, ...clonedMeshes];
    }

    private moveMeshes(position: Vector3) {
        this.rootPosition = position.clone();

        // let meshes = this.meshes.filter((x) => x.position.isNonUniform);
        // if (!meshes.length) { meshes = [this.meshes[this.meshes.length - 1]]; }
        // meshes.forEach((x) => x.position = position.clone());
    }

    private defineType() {
        let type = ElementType.OTHER;
        this.meshes.forEach((mesh) => {
            const name = mesh.name;
            if (name.includes('pierscien')) {
                type = ElementType.KEA_RING;
                return;
            }
            if (name.includes('zaglowek')) {
                type = ElementType.BEDHEAD;
                return;
            }
            if (name.includes('korpus') || name.includes('blat')) {
                type = ElementType.BODY;
                return;
            }
            if (name.includes('slot')) {
                type = ElementType.SLOT;
                return;
            }
            if (name.includes('materac')) {
                type = ElementType.MATTRESS;
                return;
            }
            if (name[0] === 'f') {
                type = ElementType.FRONT;
                if (name.includes('0,5')) {
                    this._specyficType = SpecyficType.DRAWER_FRONT;
                } else {
                    this._specyficType = SpecyficType.REGULAR_FRONT;
                }
                return;
            }
            if (name.includes('n_baza')) {
                type = ElementType.LEGS_BASE;
                return;
            }
            if (name[0] === 'n' || name.includes('_n_')) {
                type = ElementType.LEGS;
                return;
            }
            if (name.includes('sn_s_') || name.includes('b_s_')) {
                this._specyficType = SpecyficType.DRAWER;
                type = ElementType.INSIDE;
                return;
            }
            if (name.includes('sn_f_') || name.includes('b_f_')) {
                type = ElementType.FRONT;
                return;
            }
            if (name[0] === 's') {
                this._specyficType = SpecyficType.DRAWER;
                type = ElementType.INSIDE;
                return;
            }
            if (name[0] === 'd') {
                type = ElementType.INSIDE;
                this._specyficType = SpecyficType.HANGER;
                return;
            }
            if (name.includes('polka')) {
                type = ElementType.INSIDE;
                this._specyficType = SpecyficType.SHELF;
                return;
            }
            if (name.includes('b_ppio')) {
                type = ElementType.WALL;
                this._specyficType = SpecyficType.VERTICAL_WALL;
                return;
            }
            if (name[0] === 'p') {
                type = ElementType.WALL;
                this._specyficType = name.includes('ppio') ?
                    SpecyficType.VERTICAL_WALL :
                    SpecyficType.HORIZONTAL_WALL;
                return;
            }
            if (name.includes('listwa_podp')) {
                type = ElementType.WALL_SUPPORT;
                return;
            }
            if (name[0] === 'u') {
                type = ElementType.HANDLE;
                return;
            }
            if (name[0] === 'z') {
                type = ElementType.HINGE;
                return;
            }
        });
        if (this.model === 'back') {
            type = ElementType.BACK;
        }
        this._type = type;
    }

    private setVisibility() {
        if (this.type === ElementType.FRONT ||
            this.type === ElementType.HANDLE) {
            this.isVisible = store.getters.scene.areFrontsVisible;
        }
    }

    private setPrice() {

    }

    protected setBoundingInfo() {
        if (this.meshes.length === 0) { return; }
        const bounding = (this.root.getChildren()[0] as TransformNode).getHierarchyBoundingVectors();
        this._width = bounding.max.x - bounding.min.x;
        this._height = bounding.max.y - bounding.min.y;
        this._depth = bounding.max.z - bounding.min.z;

        // const mesh = this.meshes
        //     .find((x) => x.getBoundingInfo().boundingBox.centerWorld.isNonUniform)!;
        // const boundingBox = mesh.getBoundingInfo().boundingBox;
        // if (boundingBox === undefined) { return; }
        // const min = boundingBox.minimumWorld;
        // const max = boundingBox.maximumWorld;
        // this._width = max.x - min.x;
        // this._height = max.y - min.y;
        // this._depth = max.z - min.z;
    }
}
